/**
 * Gift — light on foil, not a picture of a ribbon.
 *
 * What makes a wrapped thing feel precious is the way its surface rakes a
 * highlight across itself when it tilts. That is a gesture rather than a shape,
 * so this effect draws no iconography at all: a warmth that enters from the
 * leading edge and holds, and one wide, soft, skewed highlight that crosses the
 * box every fourteen seconds and is gone. On a row an operator reads all day,
 * a shape competes with the data forever while a slow sweep is noticed once.
 *
 * Three geometry decisions this effect does NOT share with its siblings:
 *
 *   - Its own SVG carries no viewBox, so one user unit is one CSS pixel and the
 *     highlight's skew stays a skew. The shared `createSVG()` maps a 0–100
 *     square onto the host with `preserveAspectRatio="none"`, which on a
 *     1557×104 list row is a 15:1 shear.
 *   - It sets `isolation: isolate` on the host and sits at `z-index: -1`, so it
 *     paints above the host's own background and beneath its in-flow content.
 *     Without the isolation the negative layer escapes to the nearest ancestor
 *     stacking context and hides behind the card entirely — rendering nothing.
 *   - Its alpha budget is chosen from the host's measured ground luminance:
 *     one alpha cannot read on both `#1c1b1f` and `#fffbfe`.
 */

import type { ArtState } from '../types'
import { createOverlayContainer } from '../utils'

/** The highlight's crossing, and the rest between crossings. */
const SWEEP_TRAVEL_MS = 2800
const SWEEP_CYCLE_MS = 28000

/**
 * Each instance enters the cycle at a golden-ratio stride, so N rows spread
 * across it without ever tiling evenly. A fixed millisecond stride would
 * overlap once `calm` stretched the cycle out from under it.
 *
 * The stride's closest pair at ten rows sits ~5.6% of the cycle apart, which
 * is what sets `SWEEP_CYCLE_MS`: below ~26 s a ten-row console has two rows
 * crossing at once, and the operator's periphery never settles.
 */
const PHASE_STRIDE = 0.618

/** A raking angle — light crosses a tilted surface, never square to it. */
const SWEEP_SKEW_DEG = -18

/** Gradient ids resolve per document, so each mounted instance needs its own. */
let instanceSerial = 0

export function createGiftOverlay(state: ArtState): void {
	const { element, color } = state

	element.style.isolation = 'isolate'

	const overlay = createOverlayContainer('gift-overlay', element)
	/** Beneath the host's own content, unlike every sibling effect's 1000. */
	overlay.style.zIndex = '-1'

	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
	svg.setAttribute('width', '100%')
	svg.setAttribute('height', '100%')
	svg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%'

	const serial = instanceSerial++
	const warmthFill = `schmancy-gift-warmth-${serial}`
	const sweepFill = `schmancy-gift-sweep-${serial}`

	const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs')

	for (const gradient of [
		/** The warmth hugs the leading edge — the gold border bleeding inward, never a wash across the card. */
		{ id: warmthFill, stops: [{ at: 0, alpha: 1 }, { at: 0.12, alpha: 0.28 }, { at: 0.4, alpha: 0 }] },
		{ id: sweepFill, stops: [{ at: 0, alpha: 0 }, { at: 0.5, alpha: 1 }, { at: 1, alpha: 0 }] },
	]) {
		const node = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient')
		node.setAttribute('id', gradient.id)
		node.setAttribute('x1', '0')
		node.setAttribute('y1', '0')
		node.setAttribute('x2', '1')
		node.setAttribute('y2', '0')

		for (const stop of gradient.stops) {
			const node2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
			node2.setAttribute('offset', stop.at.toString())
			node2.setAttribute('stop-color', color)
			node2.setAttribute('stop-opacity', stop.alpha.toString())
			node.appendChild(node2)
		}

		defs.appendChild(node)
	}

	svg.appendChild(defs)

	const warmth = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
	warmth.setAttribute('x', '0')
	warmth.setAttribute('y', '0')
	warmth.setAttribute('width', '100%')
	warmth.setAttribute('height', '100%')
	warmth.setAttribute('fill', `url(#${warmthFill})`)
	warmth.setAttribute('opacity', '0')
	warmth.style.willChange = 'opacity'
	svg.appendChild(warmth)

	const sweep = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
	sweep.setAttribute('fill', `url(#${sweepFill})`)
	sweep.setAttribute('opacity', '0')
	sweep.style.willChange = 'transform, opacity'
	svg.appendChild(sweep)

	overlay.appendChild(svg)
	element.appendChild(overlay)

	state.overlayElement = overlay
	state.gift = {
		warmth,
		sweep,
		warmthAlpha: 0,
		sweepAlpha: 0,
		width: 0,
		height: 0,
		sweepWidth: 0,
		calm: 1,
		phaseFraction: (serial * PHASE_STRIDE) % 1,
		lastMeasure: 0,
		reducedMotion:
			typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
		drawnStatic: false,
		startTime: performance.now(),
	}
}

export function animateGift(state: ArtState, currentTime: number): void {
	if (!state.gift) return

	const { intensity = 1, speed = 1, element } = state
	const gift = state.gift
	const { warmth, sweep } = gift

	/**
	 * A layout read at 1 Hz, never per frame. `width === 0` is the never-measured
	 * test rather than the elapsed one, because a card mounted in the page's first
	 * second has a `currentTime` under the interval and would paint a frame of
	 * zero-geometry highlight.
	 */
	if (gift.width === 0 || currentTime - gift.lastMeasure > 1000) {
		gift.lastMeasure = currentTime

		/**
		 * The nearest painted ancestor decides the alpha budget; gold reads
		 * differently on each ground. Climbing through `getRootNode().host` is
		 * load-bearing: `parentElement` is null for the first child of a shadow
		 * root, which is exactly where a card slot puts this. Re-read every tick
		 * so a theme toggle after mount does not strand the budget.
		 */
		let groundIsLight = true
		for (let node: Element | null = element; node; ) {
			const channels = getComputedStyle(node).backgroundColor.match(/[\d.]+/g)
			if (channels && channels.length >= 3 && (channels.length < 4 || Number(channels[3]) > 0.1)) {
				groundIsLight =
					(0.2126 * Number(channels[0]) + 0.7152 * Number(channels[1]) + 0.0722 * Number(channels[2])) / 255 > 0.5
				break
			}
			const root = node.getRootNode()
			node = node.parentElement ?? (root instanceof ShadowRoot ? root.host : null)
		}

		gift.warmthAlpha = groundIsLight ? 0.16 : 0.13
		gift.sweepAlpha = groundIsLight ? 0.17 : 0.2

		const width = element.clientWidth || 1
		const height = element.clientHeight || 1

		if (width !== gift.width || height !== gift.height) {
			gift.width = width
			gift.height = height
			/** Wide enough to have no findable edge, narrow enough to read as light rather than as a tint. */
			gift.sweepWidth = Math.max(90, width * 0.32)
			/** A short row is quieter than a tall panel by construction — ten rows must not add up to a light show. */
			gift.calm = Math.max(0.35, Math.min(1, height / 200))

			sweep.setAttribute('x', '0')
			sweep.setAttribute('y', (-height).toString())
			sweep.setAttribute('width', gift.sweepWidth.toFixed(1))
			sweep.setAttribute('height', (height * 3).toFixed(1))

			gift.drawnStatic = false
		}
	}

	if (intensity <= 0) {
		warmth.setAttribute('opacity', '0')
		sweep.setAttribute('opacity', '0')
		return
	}

	if (gift.reducedMotion) {
		if (gift.drawnStatic) return

		warmth.setAttribute('opacity', (gift.warmthAlpha * intensity).toFixed(3))
		sweep.style.transform = `translate(${(gift.width * 0.4).toFixed(1)}px, 0px) skewX(${SWEEP_SKEW_DEG}deg)`
		sweep.setAttribute('opacity', (gift.sweepAlpha * 0.6 * intensity).toFixed(3))

		gift.drawnStatic = true
		return
	}

	const elapsed = (currentTime - gift.startTime) * speed

	/** The warmth only breathes — a ±12% swing on a ~13 s period, under the threshold of being watched. */
	warmth.setAttribute(
		'opacity',
		(gift.warmthAlpha * intensity * (0.88 + Math.sin(elapsed * 0.0005) * 0.12)).toFixed(3),
	)

	const cycle = SWEEP_CYCLE_MS / gift.calm
	const phase = (elapsed + gift.phaseFraction * cycle) % cycle

	if (phase > SWEEP_TRAVEL_MS) {
		sweep.setAttribute('opacity', '0')
		return
	}

	/** Smoothstep — a highlight enters and leaves a surface, it never travels at a constant rate. */
	const progress = phase / SWEEP_TRAVEL_MS
	const eased = progress * progress * (3 - 2 * progress)
	const overhang = gift.height * Math.tan((-SWEEP_SKEW_DEG * Math.PI) / 180)
	const distance = -gift.sweepWidth - overhang + eased * (gift.width + gift.sweepWidth + overhang * 2)

	sweep.style.transform = `translate(${distance.toFixed(1)}px, 0px) skewX(${SWEEP_SKEW_DEG}deg)`
	sweep.setAttribute('opacity', (Math.sin(progress * Math.PI) * gift.sweepAlpha * intensity).toFixed(3))
}
